/* eslint-disable no-unused-vars */
/* eslint-disable indent */
/* eslint-disable no-unused-vars */
/* eslint-disable*/
import React, { useState, useEffect } from "react";
import { Card, Row, Col, message, Button } from "antd";

import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent,
    RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList, getHubCPList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadCpOutscanVsHubInscanReport } from "../../../../utils/downloadUtils";
import { useNavigate } from "react-router-dom";

const CpOutscanVsHubInscanReport = () => {
    const FILTER_STORAGE_KEY = "CP_OUTSCAN_VS_HUB_INSCAN_REPORT_FILTERS";

    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [cpLoading, setCpLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        moment().subtract(1, "days"),
        moment()
    ]);
    const [hubList, setHubList] = useState([]);
    const [cpList, setCpList] = useState([]);
    const [selectedHubs, setSelectedHubs] = useState([]);
    const [selectedCPs, setSelectedCPs] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [hubUser, setHubUser] = useState();
    const navigate = useNavigate();
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);

    // Helper function to save filters to sessionStorage
    const saveFiltersToStorage = () => {
        const filters = {
            dateRange: dateRange ? [
                dateRange[0]?.format("YYYY-MM-DD"),
                dateRange[1]?.format("YYYY-MM-DD")
            ] : null,
            selectedHubs,
            selectedCPs,
            pagination: {
                current: pagination.current,
                pageSize: pagination.pageSize
            }
        };
        sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
    };

    // Helper function to restore filters from sessionStorage
    const restoreFiltersFromStorage = () => {
        try {
            const savedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);
            if (savedFilters) {
                const filters = JSON.parse(savedFilters);
                if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
                    setDateRange([
                        moment(filters.dateRange[0]),
                        moment(filters.dateRange[1])
                    ]);
                }
                if (filters.selectedHubs && filters.selectedHubs.length > 0) {
                    setSelectedHubs(filters.selectedHubs);
                }
                if (filters.selectedCPs && filters.selectedCPs.length > 0) {
                    setSelectedCPs(filters.selectedCPs);
                }
                if (filters.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        current: filters.pagination.current || prev.current,
                        pageSize: filters.pagination.pageSize || prev.pageSize
                    }));
                }
                // Clear the saved filters after restoring
                sessionStorage.removeItem(FILTER_STORAGE_KEY);
                return true;
            }
        } catch (error) {
            console.error("Error restoring filters:", error);
        }
        return false;
    };

    // Fetch hub list on mount
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const hubs = await getHubList();
                setHubList(hubs);
                // Restore filters after hub list is loaded
                restoreFiltersFromStorage();
            } catch {
                message.error("Failed to fetch hub list");
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line
    }, []);

    // Set default hub for HUBADMIN users (only if no hubs are already selected and no filters were restored)
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
        let userid = JSON.parse(localStorage.getItem("userId") || "{}");
        setHubUser(user?.roles?.[0]);

        const activeHub = JSON.parse(localStorage.getItem("active_hub"));
        const activeHubId = activeHub?.hubId;

        // Check if there are saved filters - if yes, don't set default hub (restore will handle it)
        const hasSavedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);
        // Only set default hub if no hubs are selected and no filters were saved (to avoid overriding restored filters)
        if (hubList.length > 0 && selectedHubs.length === 0 && !hasSavedFilters) {
            if (activeHubId) {
                const defaultHub = hubList.find((hub) => String(hub.value) === String(activeHubId));
                if (defaultHub) {
                    setSelectedHubs([defaultHub.value]);
                }
            } else if (user?.roles?.[0] === "ROLE_HUBADMIN" || user?.roles?.[0] === "ROLE_HUBOPERATOR") {
                const defaultHub = hubList.find(hub => String(hub.value) === String(userid?.userInfo?.premiseId));
                if (defaultHub) {
                    setSelectedHubs([defaultHub.value]);
                }
            }
        }
    }, [hubList]);

    // Fetch CP list when hubs are selected
    useEffect(() => {
        if (!selectedHubs || selectedHubs.length === 0) {
            setCpList([]);
            setSelectedCPs([]);
            return;
        }

        (async () => {
            try {
                setCpLoading(true);
                const cpLists = await Promise.all(selectedHubs.map(hubId => getHubCPList(hubId)));
                const allCps = [...new Set(cpLists.flat())];
                setCpList(allCps);
            } catch (error) {
                console.error("Failed to fetch CP list", error);
                message.error("Failed to fetch CP list");
                setCpList([]);
            } finally {
                setCpLoading(false);
            }
        })();
    }, [selectedHubs]);

    // Fetch report data when filters change
    useEffect(() => {
        if (!hubUser) {
            return;
        }
        if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedHubs.length === 0) {
            return;
        }
        fetchReport();
        // eslint-disable-next-line
    }, [hubUser, selectedHubs, selectedCPs, dateRange, pagination.current, pagination.pageSize]);

    const fetchReport = async () => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
            message.warning("Please select date range");
            return;
        }

        setLoading(true);
        try {
            const params = {
                fromDate: dateRange[0].format("YYYY-MM-DD"),
                toDate: dateRange[1].format("YYYY-MM-DD"),
                hubIds: selectedHubs,
                cpIds: selectedCPs,
                pageNum: pagination.current - 1,
                pageSize: pagination.pageSize
            };

            console.log("Fetching CP outscan vs hub inscan report with params:", params);
            const response = await ReportsService.getCpOutscanVsHubInscanReport(params);
            console.log("CP outscan vs hub inscan report response:", response);

            if (response?.lastRefreshedAt) {
                setLastsynced(response.lastRefreshedAt);
            }
            if (response?.nextScheduledRefresh) {
                setNextScheduledRefresh(response.nextScheduledRefresh);
            }
            if (response?.data) {
                setReportData(response.data);
                setPagination(prev => ({
                    ...prev,
                    total: response.total || 0,
                    current: (response.page || 0) + 1,
                    pageSize: response.pageSize || 20
                }));
            } else {
                setReportData([]);
                setPagination(prev => ({
                    ...prev,
                    total: 0
                }));
                message.error("No data available");
            }
        } catch (error) {
            console.error("Error fetching CP outscan vs hub inscan report:", error);
            message.error(error?.message || "Failed to fetch report data");
            setReportData([]);
            setPagination(prev => ({
                ...prev,
                total: 0
            }));
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };

    const handleDownload = async () => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
            message.warning("Please select date range");
            return;
        }
        setDownloadLoading(true);
        try {
            const downloadParams = {
                fromDate: dateRange[0].format("YYYY-MM-DD"),
                toDate: dateRange[1].format("YYYY-MM-DD"),
                hubIds: selectedHubs,
                cpIds: selectedCPs
            };

            await downloadCpOutscanVsHubInscanReport(downloadParams);
            message.success("Download started successfully");
        } catch (error) {
            console.error("Error downloading CP outscan vs hub inscan report:", error);
            message.error("Failed to download report");
        } finally {
            setDownloadLoading(false);
        }
    };
    const handleClickableReport = (type, record) => {
        // Save current filters before navigating
        saveFiltersToStorage();

        if (!record.date) {
            message.error("Please select Date");
            return;
        }
        if (!record.fromCpId) {
            message.error("Missing Booking CP Id");
            return;
        }
        if (!record.toHubId) {
            message.error("Missing Hub Id");
            return;
        }
        if (type === "outscanByCp") {
            navigate("/cp-outscan-clickable-report", {
                state: {
                    operationDate: record.date,
                    bookingCpId: record.fromCpId,
                    hubId: record.toHubId
                }
            });
        }
        if (type === "totalInscanByHub") {
            navigate("/hub-inscan-clickable-report", {
                state: {
                    operationDate: record.date,
                    hubId: record.toHubId,
                    bookingCpId: record.fromCpId
                }
            });
        }
    }

    const columns = [
        {
            title: "No.",
            dataIndex: "index",
            key: "index",
            width: 60,
            render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
            sorter: true,
            width: 120,
            render: (date) => date || "-"
        },
        {
            title: "To Hub Name",
            dataIndex: "toHubName",
            key: "toHubName",
            sorter: true,
            width: 200,
            render: (toHubName) => toHubName || "-"
        },
        {
            title: "From CP Name",
            dataIndex: "fromCpName",
            key: "fromCpName",
            sorter: true,
            width: 200,
            render: (fromCpName) => fromCpName || "-"
        },
        {
            title: "Outscan by CP",
            dataIndex: "outscanByCp",
            key: "outscanByCp",
            sorter: true,
            width: 120,
            render: (outscanByCp, record) => (
                outscanByCp && outscanByCp > 0 ? (
                    <span
                        style={{
                            color: "#1890ff",
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: "bold"
                        }}
                        onClick={() => {
                            handleClickableReport("outscanByCp", record);
                        }}
                    >
                        {outscanByCp}
                    </span>
                ) : <span>{outscanByCp || 0}</span>
            )
        },
        {
            title: "Total Inscan by Hub",
            dataIndex: "totalInscanByHub",
            key: "totalInscanByHub",
            sorter: true,
            width: 150,
            render: (totalInscanByHub, record) => (
                totalInscanByHub && totalInscanByHub > 0 ? (
                    <span
                        style={{
                            color: "#1890ff",
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: "bold"
                        }}
                        onClick={() => {
                            handleClickableReport("totalInscanByHub", record);
                        }}
                    >
                        {totalInscanByHub}
                    </span>
                ) : <span>{totalInscanByHub || 0}</span>
            )
        },

    ];

    return (
        <div className="report-container">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading
                    title="CP Outscan vs Hub Inscan Report"
                    navigateTo="/reports"
                >
                    <div style={{ paddingTop: "5px" }}>
                        <RefreshTimestamp
                            lastRefreshedAt={lastsynced}
                            nextScheduledRefresh={nextScheduledRefresh}
                        />
                    </div>
                </Heading>
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Date Range</div>
                        <CalendarComponent
                            className="custom-calendar"
                            type="range"
                            value={dateRange}
                            onChange={(value) => {
                                setDateRange(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Hubs</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Hubs"
                            options={hubList}
                            onChange={(value) => {
                                setSelectedHubs(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedHubs}
                            mode={hubUser !== "ROLE_HUBADMIN" || hubUser !== "ROLE_HUBOPERATOR" ? "multiple" : "default"}
                            allowClear
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                            disabled={hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR"}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">CPs</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder={cpLoading ? "Loading CPs..." : cpList.length === 0 ? "Select Hub first" : "Select CPs"}
                            options={cpList}
                            onChange={(value) => {
                                setSelectedCPs(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedCPs}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            disabled={cpLoading || cpList.length === 0}
                            loading={cpLoading}
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6} style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                        <Button
                            type="primary"
                            onClick={handleDownload}
                            loading={downloadLoading}
                            icon={<DownloadOutlined />}
                            disabled={!dateRange || !dateRange[0] || !dateRange[1]}
                        >
                            Download Report
                        </Button>
                    </Col>
                </Row>

                <div className="table-section">
                    <TableComponent
                        columns={columns}
                        dataSource={reportData}
                        loading={loading}
                        pagination={pagination}
                        onChange={handleTableChange}
                        rowKey={(record, index) => `${record.date}_${record.toHubId}_${index}`}
                        scroll={{ x: 1200 }}
                    />
                </div>
            </Card>
        </div>
    );
};

export default CpOutscanVsHubInscanReport;
